import { create } from 'zustand';
import {
  expensesAPI,
  Expense,
  ExpenseCreate,
  ExpenseUpdate,
} from '../services/api';

interface ExpenseState {
  expenses: Expense[];
  isLoading: boolean;
  error: string | null;

  fetchExpenses: (params?: {
    date?: string;
    start_date?: string;
    end_date?: string;
    project_id?: string;
    client_id?: string;
    expense_category_id?: string;
  }) => Promise<void>;
  createExpense: (data: ExpenseCreate) => Promise<Expense>;
  updateExpense: (id: string, data: ExpenseUpdate) => Promise<Expense>;
  deleteExpense: (id: string) => Promise<void>;
  clearError: () => void;
}

const getErrorMessage = (error: any, defaultMsg: string): string => {
  const detail = error.response?.data?.detail;
  if (Array.isArray(detail)) {
    return detail.map((e: any) => `${e.loc?.slice(1).join('.')}: ${e.msg}`).join(', ') || defaultMsg;
  }
  if (typeof detail === 'string') return detail;
  return defaultMsg;
};

export const useExpenseStore = create<ExpenseState>((set) => ({
  expenses: [],
  isLoading: false,
  error: null,

  fetchExpenses: async (params) => {
    set({ isLoading: true, error: null });
    try {
      const expenses = await expensesAPI.list(params);
      set({ expenses, isLoading: false });
    } catch (error: any) {
      set({ error: getErrorMessage(error, 'Failed to fetch expenses'), isLoading: false });
    }
  },

  createExpense: async (data: ExpenseCreate) => {
    set({ isLoading: true, error: null });
    try {
      const expense = await expensesAPI.create(data);
      set((state) => ({ expenses: [...state.expenses, expense], isLoading: false }));
      return expense;
    } catch (error: any) {
      set({ error: getErrorMessage(error, 'Failed to create expense'), isLoading: false });
      throw error;
    }
  },

  updateExpense: async (id: string, data: ExpenseUpdate) => {
    set({ isLoading: true, error: null });
    try {
      const expense = await expensesAPI.update(id, data);
      set((state) => ({
        expenses: state.expenses.map((e) => (e.id === id ? expense : e)),
        isLoading: false,
      }));
      return expense;
    } catch (error: any) {
      set({ error: getErrorMessage(error, 'Failed to update expense'), isLoading: false });
      throw error;
    }
  },

  deleteExpense: async (id: string) => {
    set({ isLoading: true, error: null });
    try {
      await expensesAPI.delete(id);
      set((state) => ({
        expenses: state.expenses.filter((e) => e.id !== id),
        isLoading: false,
      }));
    } catch (error: any) {
      set({ error: getErrorMessage(error, 'Failed to delete expense'), isLoading: false });
      throw error;
    }
  },

  clearError: () => set({ error: null }),
}));
